import { z } from 'zod';

const numericRegex = /^\d+$/;
const decimalRegex = /^\d+(\.\d{1,2})?$/;

/**
 * customRate accepts either a number (>= 0) or a string decimal like "120" / "120.50".
 * Always transforms to a string with up to 2 decimal places so the API layer has a
 * single canonical shape matching Prisma's Decimal(10, 2) serialization.
 */
const customRateSchema = z
  .union([
    z.number().nonnegative('Rate must be zero or greater'),
    z.string().trim().regex(decimalRegex, 'Rate must be a decimal with up to 2 decimal places'),
  ])
  .transform((v) => (typeof v === 'number' ? v.toFixed(2) : v));

export const agreementCreateSchema = z
  .object({
    includedIncrements: z
      .number()
      .int('Included increments must be a whole number')
      .min(0, 'Included increments must be zero or greater'),
    customRate: customRateSchema,
    cycleDay: z
      .number()
      .int('Cycle day must be a whole number')
      .min(1, 'Cycle day must be between 1 and 28')
      .max(28, 'Cycle day must be between 1 and 28')
      .default(27),
    startDate: z.string().datetime('Start date must be an ISO date-time'),
    endDate: z.string().datetime('End date must be an ISO date-time').optional(),
    gaPropertyId: z.string().regex(numericRegex, 'GA4 property ID must be numeric').optional(),
    legacyPlanRef: z
      .string()
      .trim()
      .max(80, 'Legacy plan reference must be 80 characters or fewer')
      .optional(),
    websiteId: z.string().min(1).nullable().optional(),
  })
  .strict();
export type AgreementCreateInput = z.infer<typeof agreementCreateSchema>;

export const agreementUpdateSchema = agreementCreateSchema
  .partial()
  .extend({
    isActive: z.boolean().optional(),
  })
  .strict();
export type AgreementUpdateInput = z.infer<typeof agreementUpdateSchema>;

export const agreementSummarySchema = z.object({
  id: z.string(),
  companyId: z.string(),
  includedIncrements: z.number().int(),
  customRate: z.string(),
  cycleDay: z.number().int(),
  startDate: z.string().datetime(),
  endDate: z.string().datetime().nullable(),
  gaPropertyId: z.string().nullable(),
  legacyPlanRef: z.string().nullable(),
  websiteId: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type AgreementSummary = z.infer<typeof agreementSummarySchema>;
